import AppHeader from "@/components/AppHeader";
import { DashboardItem } from "@/components/DashboardItem";
import { FilterPanel, type PropertyFilterGroup } from "@/components/FilterPanel";
import { useAuth } from "@/hooks/useAuth";
import { useCapabilities } from "@/hooks/useCapabilities";
import {
  useArchiveDashboard,
  useDashboards,
  useDeleteDashboard,
  useSetDashboardTags,
  useUpdateDashboard,
} from "@/hooks/useDashboards";
import { useProperties } from "@/hooks/useProperties";
import { useSiteLogo } from "@/hooks/useSiteLogo";
import { useSparkEnabled } from "@/hooks/useSparkEnabled";
import { useStarredDashboards, useToggleStar } from "@/hooks/useStarredDashboards";
import { useTenant } from "@/hooks/useTenant";
import { useTrack } from "@/hooks/useTrack";
import { useGroupDashboardIds, useUserGroups } from "@/hooks/useUserGroups";
import { useUserRole } from "@/hooks/useUserRole";
import {
  activeFilterCount as calcActiveFilterCount,
  filterAndSortDashboards,
} from "@/lib/dashboardFilters";
import { CreateSparkDashboardDialog } from "@/spark/CreateSparkDashboardDialog";
import { Button } from "@simplicity/ui";
import { Filter, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";

// ─── Types ──────────────────────────────────────────────────────────────────

interface FilterPanelConfig {
  searchQuery: string;
  onSearch: (q: string) => void;
  userGroups: Array<{ id: string; name: string }>;
  selectedGroupId: string | null;
  onSelectGroup: (id: string | null) => void;
  propertyFilters: PropertyFilterGroup[];
  onTogglePropertyValue: (propertyKey: string, value: string) => void;
}

// ─── Hooks ──────────────────────────────────────────────────────────────────

function useFilterState(isAdmin: boolean) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  // SIM-1420 (slice 4) — year/category live in `selectedPropertyValues`
  // under the `school_year` and `category` keys now; the previous
  // standalone selectedSchoolYears / selectedCategories state slices were
  // removed when their PropertyDef rows became canonical (slice 3
  // migration).
  const [selectedPropertyValues, setSelectedPropertyValues] = useState<Record<string, string[]>>(
    {},
  );

  const { data: userGroups = [] } = useUserGroups();
  const { data: groupDashboardIds } = useGroupDashboardIds(selectedGroupId);
  const { data: dashboards = [], isLoading: dashboardsLoading } = useDashboards();
  const { starredIds } = useStarredDashboards();
  // SIM-1464 — year/category option lists are derived from PropertyDef.
  // The legacy `useSettingList("school_years"/"categories")` reads (and
  // the PortalSetting rows they targeted) were removed in this slice.
  const { data: properties } = useProperties();

  const filtered = useMemo(
    () =>
      filterAndSortDashboards(dashboards, {
        searchQuery,
        selectedGroupId,
        groupDashboardIds,
        starredIds,
        selectedPropertyValues,
      }),
    [
      searchQuery,
      dashboards,
      selectedGroupId,
      groupDashboardIds,
      starredIds,
      selectedPropertyValues,
    ],
  );

  const handleTogglePropertyValue = useCallback((propertyKey: string, value: string) => {
    setSelectedPropertyValues((prev) => {
      const current = prev[propertyKey] ?? [];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return { ...prev, [propertyKey]: next };
    });
  }, []);

  const activeFilterCount = calcActiveFilterCount(
    selectedGroupId,
    searchQuery,
    selectedPropertyValues,
  );

  const propertyFilters: PropertyFilterGroup[] = properties.map((p) => ({
    key: p.key,
    label: p.label,
    options: p.options,
    selectedValues: selectedPropertyValues[p.key] ?? [],
  }));

  const filterConfig: FilterPanelConfig = {
    searchQuery,
    onSearch: setSearchQuery,
    userGroups: isAdmin ? userGroups : [],
    selectedGroupId,
    onSelectGroup: setSelectedGroupId,
    propertyFilters,
    onTogglePropertyValue: handleTogglePropertyValue,
  };

  return {
    filtered,
    dashboardsLoading,
    starredIds,
    activeFilterCount,
    filterConfig,
    searchQuery,
    selectedCategoryValues: selectedPropertyValues.category ?? [],
    properties,
  };
}

function useDashboardHandlers() {
  const { data: dashboards = [] } = useDashboards();
  const updateDashboard = useUpdateDashboard();
  const archiveDashboard = useArchiveDashboard();
  const deleteDashboard = useDeleteDashboard();
  const setDashboardTags = useSetDashboardTags();
  const toggleStar = useToggleStar();

  return {
    onEdit: useCallback(
      (
        id: string,
        updates: { title: string; url: string; school_year: string; category: string },
      ) => updateDashboard.mutate({ id, ...updates }),
      [updateDashboard],
    ),
    onEditTags: useCallback(
      (id: string, tags: Array<{ propertyKey: string; value: string }>) =>
        setDashboardTags.mutate({ dashboardId: id, tags }),
      [setDashboardTags],
    ),
    onArchive: useCallback(
      (id: string) => {
        const dash = dashboards.find((d) => d.id === id);
        archiveDashboard.mutate({ id, archived: !dash?.archived });
      },
      [archiveDashboard, dashboards],
    ),
    onDelete: useCallback((id: string) => deleteDashboard.mutate(id), [deleteDashboard]),
    onToggleStar: useCallback(
      (dashboardId: string, currentlyStarred: boolean) =>
        toggleStar.mutate({ dashboardId, starred: !currentlyStarred }),
      [toggleStar],
    ),
  };
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function MobileFilterOverlay({
  open,
  onClose,
  config,
}: {
  open: boolean;
  onClose: () => void;
  config: FilterPanelConfig;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 md:hidden">
      <button
        type="button"
        aria-label="Close filters"
        className="absolute inset-0 bg-black/40 cursor-default"
        onClick={onClose}
      />
      <div className="absolute left-0 top-0 bottom-0 w-[280px] max-w-[85vw] pl-4 overflow-y-auto bg-muted">
        <div className="flex items-center justify-between pr-4 pt-4">
          <span className="text-xs font-semibold text-muted-foreground">Filters</span>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>
        <FilterPanel {...config} />
      </div>
    </div>
  );
}

function DashboardGridSection({
  filtered,
  canManageDashboards,
  handlers,
  starredIds,
  properties,
  tenantPath,
  onOpenSparkDialog,
  sparkEnabled,
}: {
  filtered: ReturnType<typeof filterAndSortDashboards>;
  /** SIM-3113: add/edit/delete of dashboards is super-admin only. Tenant
   *  admins keep user, group and dashboard-assignment management. */
  canManageDashboards: boolean;
  handlers: ReturnType<typeof useDashboardHandlers>;
  starredIds: string[];
  properties: ReturnType<typeof useProperties>["data"];
  tenantPath: (p: string) => string;
  onOpenSparkDialog: () => void;
  sparkEnabled: boolean;
}) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-x-8 sm:gap-y-10">
      {filtered.map((d, i) => (
        <DashboardItem
          key={d.id}
          dashboard={d}
          index={i}
          onEdit={canManageDashboards ? handlers.onEdit : undefined}
          onEditTags={canManageDashboards ? handlers.onEditTags : undefined}
          properties={properties}
          onArchive={canManageDashboards ? handlers.onArchive : undefined}
          onDelete={canManageDashboards ? handlers.onDelete : undefined}
          isStarred={starredIds.includes(d.id)}
          onToggleStar={handlers.onToggleStar}
        />
      ))}
      {canManageDashboards && (
        <>
          {sparkEnabled && (
            <button
              type="button"
              onClick={onOpenSparkDialog}
              className="block group border border-border rounded shadow-md bg-card hover:border-primary transition-colors flex flex-col items-center justify-center min-h-[200px]"
            >
              <span className="text-3xl mb-2 text-muted-foreground">&#9889;</span>
              <span className="text-sm font-medium text-muted-foreground">New SimplySpark</span>
            </button>
          )}
          <Link
            to={tenantPath("/add-dashboard")}
            className="block group border border-border rounded shadow-md bg-card hover:border-primary transition-colors flex flex-col items-center justify-center min-h-[200px]"
          >
            <span className="text-5xl mb-2 text-muted-foreground">+</span>
            <span className="text-sm font-medium text-muted-foreground">Add Dashboard</span>
          </Link>
        </>
      )}
    </div>
  );
}

function MobileFilterButton({ onClick, count }: { onClick: () => void; count: number }) {
  return (
    <Button variant="outline" size="sm" className="md:hidden mb-4 gap-2" onClick={onClick}>
      <Filter className="h-4 w-4" />
      Filters
      {count > 0 && (
        <span className="bg-primary text-primary-foreground text-xs rounded-full h-5 w-5 flex items-center justify-center">
          {count}
        </span>
      )}
    </Button>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────

function DashboardResultsCaption({
  dashboardsLoading,
  filtered,
  searchQuery,
  selectedCategoryValues,
}: {
  dashboardsLoading: boolean;
  filtered: ReadonlyArray<unknown>;
  searchQuery: string;
  selectedCategoryValues: ReadonlyArray<string>;
}) {
  return (
    <>
      <div className="text-lg font-medium text-muted-foreground mb-6 sm:mb-8">
        {dashboardsLoading ? (
          "Loading..."
        ) : (
          <>
            {filtered.length} {filtered.length === 1 ? "dashboard" : "dashboards"}
            {selectedCategoryValues.length > 0 && ` in ${selectedCategoryValues.join(", ")}`}
          </>
        )}
      </div>
      {!dashboardsLoading && filtered.length === 0 && (
        <p className="text-sm text-muted-foreground mb-6">
          No results{searchQuery ? ` for "${searchQuery}"` : ""}
        </p>
      )}
    </>
  );
}

const Index = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { tenantPath } = useTenant();
  const { isAdmin, hasRole, isLoading: roleLoading } = useUserRole();
  const { canManageDashboards } = useCapabilities();
  const { data: siteLogo } = useSiteLogo();
  const { enabled: sparkEnabled } = useSparkEnabled();
  const handlers = useDashboardHandlers();
  const [filterOpen, setFilterOpen] = useState(false);
  const [sparkDialogOpen, setSparkDialogOpen] = useState(false);

  const {
    filtered,
    dashboardsLoading,
    starredIds,
    activeFilterCount,
    filterConfig,
    searchQuery,
    selectedCategoryValues,
    properties,
  } = useFilterState(isAdmin);

  useEffect(() => {
    if (!loading && !user) void navigate("/login", { replace: true });
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!loading && !roleLoading && user && !hasRole)
      void navigate("/unauthorized", { replace: true });
  }, [user, loading, roleLoading, hasRole, navigate]);

  const track = useTrack();
  useEffect(() => {
    track("page_view", { page: "index" });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire once on mount; depending on `track` would double-fire if its identity changes between renders.
  }, []);

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground text-sm">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <AppHeader />
      <div className="flex flex-1 relative">
        <div className="hidden md:block w-1/4 min-w-[240px] pl-8 bg-muted/30 border-r border-border">
          <FilterPanel {...filterConfig} />
        </div>
        <MobileFilterOverlay
          open={filterOpen}
          onClose={() => setFilterOpen(false)}
          config={filterConfig}
        />
        <main className="flex-1 p-4 sm:p-6 md:p-10 md:pl-2">
          <MobileFilterButton onClick={() => setFilterOpen(true)} count={activeFilterCount} />
          {siteLogo && (
            <div className="mb-4 md:-mt-10">
              <img
                src={siteLogo}
                alt="Site logo"
                className="max-h-24 max-w-[320px] object-contain"
              />
            </div>
          )}
          <DashboardResultsCaption
            dashboardsLoading={dashboardsLoading}
            filtered={filtered}
            searchQuery={searchQuery}
            selectedCategoryValues={selectedCategoryValues}
          />
          <DashboardGridSection
            filtered={filtered}
            canManageDashboards={canManageDashboards}
            handlers={handlers}
            starredIds={starredIds}
            properties={properties}
            tenantPath={tenantPath}
            onOpenSparkDialog={() => setSparkDialogOpen(true)}
            sparkEnabled={sparkEnabled}
          />
        </main>
      </div>
      <CreateSparkDashboardDialog open={sparkDialogOpen} onOpenChange={setSparkDialogOpen} />
    </div>
  );
};

export default Index;
